import { reactive } from "vue";

import { USER_KIND_CUSTOM, USER_KIND_DART_AI, USER_KIND_USER } from "~/constants/userKinds";
import { isDefined } from "~/shared/common";
import {
  AgentWorkflowAction,
  AiModel,
  EventKind,
  FolderSort,
  Language,
  PropertyKind,
  TaskDetailMode,
  Theme,
  UserRole,
  UserStatus,
} from "~/shared/enums";
import type { AgentWorkflow, AgentWorkflowUpdate, Dartboard, Task, User, UserUpdate } from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid, unionOfAllSets } from "~/utils/common";
import { userComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

const SPECIALIST_ROLES = new Set([UserRole.TECHNICAL_ADMIN, UserRole.FINANCIAL_ADMIN]);

export type State = {
  _duidsToUsers: Map<string, User>;
  _sortedUserDuids: string[];
};

type GetUserListOptions = {
  excludeAgents?: boolean;
  excludeNonagents?: boolean;
  includeDisabledAgents?: boolean;
  includeSpecialists?: boolean;
};

export const state: State = {
  _duidsToUsers: new Map(),
  _sortedUserDuids: [],
};

export type Getters = {
  getUserList: (options?: GetUserListOptions) => User[];
  getUserByDuid: (duid: string) => User | undefined;
  getUsersByDuidsOrdered: (duids: string[]) => User[];
  getUserByEmail: (email: string) => User | undefined;
  getRelevantUsersByTasks: (tasks: Task[], options?: GetUserListOptions) => User[];
  getRelevantUsersByDartboards: (dartboards: Dartboard[], options?: GetUserListOptions) => User[];
  dartAi: User;
};

export type Actions = {
  updateUser: (update: UserUpdate, options?: { awaitBackend?: boolean }) => Promise<User | undefined>;
  removeUser: (user: User, options?: { awaitBackend?: boolean }) => Promise<void>;
  createAgent: (name: string, partialAgent?: Partial<User>, options?: { awaitBackend?: boolean }) => Promise<User>;
  updateAgent: (update: UserUpdate, options?: { awaitBackend?: boolean }) => Promise<User | undefined>;
  deleteAgent: (agent: User, options?: { awaitBackend?: boolean }) => Promise<void>;
  createAgentWorkflow: (
    agentDuid: string,
    partialWorkflow?: Partial<AgentWorkflow>,
    options?: { awaitBackend?: boolean }
  ) => Promise<void>;
  updateAgentWorkflow: (
    agentDuid: string,
    update: AgentWorkflowUpdate,
    options?: { awaitBackend?: boolean }
  ) => Promise<void>;
  deleteAgentWorkflow: (agentDuid: string, duid: string, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setUsers: (users: User[]) => void;
  $createOrUpdateUser: (user: User) => void;
  $deleteUser: (user: User) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getUserList() {
    return (options = {}) => {
      const {
        excludeAgents = false,
        excludeNonagents = false,
        includeDisabledAgents = false,
        includeSpecialists = false,
      } = options;
      const userIsMemberOrGreater =
        this.$useUserStore().isRoleGte(UserRole.MEMBER) || this.$usePageStore().isPublicView;
      const effectiveExcludeAgents = excludeAgents || !this.$useUserStore().isRoleGte(UserRole.MEMBER);
      const visibleUserDuids = unionOfAllSets(this.spaceList.map((e) => new Set(e.accessibleByUserDuids)));
      return this._sortedUserDuids
        .map((e) => this._duidsToUsers.get(e))
        .filter(isDefined)
        .filter((e) => {
          const isAgent = e.kind !== USER_KIND_USER;
          return (
            (userIsMemberOrGreater || visibleUserDuids.has(e.duid) || isAgent) &&
            (isAgent ? !effectiveExcludeAgents : !excludeNonagents) &&
            (!isAgent || includeDisabledAgents || e.status !== UserStatus.AGENT_DISABLED) &&
            (includeSpecialists || !SPECIALIST_ROLES.has(e.role))
          );
        });
    };
  },
  getUserByDuid() {
    return (duid) => this._duidsToUsers.get(duid);
  },
  getUsersByDuidsOrdered() {
    return (duids) => this.getUserList().filter((e) => duids.includes(e.duid));
  },
  getUserByEmail() {
    return (email) => this.getUserList().find((e) => e.email === email);
  },
  getRelevantUsersByTasks() {
    return (tasks, options) =>
      this.getRelevantUsersByDartboards(
        this.getDartboardsByDuidsOrdered([...new Set(tasks.map((e) => e.dartboardDuid))]),
        options
      );
  },
  getRelevantUsersByDartboards() {
    return (dartboards, options) => {
      const spaceDuids = [...new Set(dartboards.map((e) => e.spaceDuid))];
      const spaces = this.getSpacesByDuidsOrdered(spaceDuids);
      if (spaces.some((e) => e.accessibleByTeam)) {
        return this.getUserList(options);
      }
      const specificAccessorDuids = [
        ...unionOfAllSets(spaces.map((e) => e.accessibleByUserDuids).map((e) => new Set(e))),
      ];
      const isMemberOrGreater = this.$useUserStore().isRoleGte(UserRole.MEMBER);
      const includeAgents = isMemberOrGreater && !options?.excludeAgents;
      const agentDuids = includeAgents ? this.getUserList({ excludeNonagents: true }).map((u) => u.duid) : [];
      return this.getUsersByDuidsOrdered([...new Set([...specificAccessorDuids, ...agentDuids])]);
    };
  },
  dartAi() {
    const user = this.getUserList({ includeDisabledAgents: true }).filter((e) => e.kind === USER_KIND_DART_AI);
    if (user.length !== 1) {
      throw new Error("Expected exactly one Dart AI user");
    }
    return user[0];
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async updateUser(update, options = {}) {
    const { awaitBackend = false } = options;
    const user = this.getUserByDuid(update.duid);
    if (!user) {
      return undefined;
    }

    Object.assign(user, update);
    updateInSorted(this._duidsToUsers, this._sortedUserDuids, user.duid, userComparator);

    const backendAction = this.$backend.user.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return user;
  },
  async removeUser(user, options = {}) {
    const { awaitBackend = false } = options;

    const userPropertyDuids = this.getPropertyByKind(PropertyKind.USER)
      .map((e) => e.duid)
      .concat([this.defaultAssigneesProperty.duid, this.defaultUpdatedByProperty.duid]);

    this.$updateLayoutsRemoveEntityDuidOrFiltersNoBackend([user.duid], userPropertyDuids);
    this.$updateDartboardsPropertyDefaultNoBackend([user.duid], userPropertyDuids);

    this.$deleteUser(user);

    const backendAction = this.$backendOld.workspace.removeUsers([user.duid]);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async createAgent(name, partialAgent = {}, options = {}) {
    const { awaitBackend = false } = options;

    const duid = makeDuid();
    const agent: User = {
      duid,
      status: UserStatus.ACTIVE,
      role: UserRole.MEMBER,
      kind: USER_KIND_CUSTOM,
      email: `agent+${duid}@dartai.com`,
      name,
      theme: Theme.SYSTEM_DEFAULT,
      colorHex: makeRandomColorHex(),
      imageUrl: null,
      timezone: null,
      calendarDuid: null,
      webhookDuid: null,
      workflows: [],
      agentConfig: {},
      taskDetailMode: TaskDetailMode.RIGHTBAR,
      sections: { expanded: [], collapsed: [] },
      layout: {
        rightbarWidthPx: 650,
        leftbarWidthPx: 225,
        fullscreenRightbarWidthPx: 600,
        chatPanelWidthPx: 400,
        docSort: FolderSort.ORDER,
      },
      authToken: null,
      isAdmin: false,
      googleData: null,
      aiLanguage: Language.ENGLISH,
      aiModel: AiModel.GPT_4_1,
      jobDescription: "",
      aiGuidelines: "",
      tutorialStatuses: {},
      ...partialAgent,
    };

    this.$createOrUpdateUser(agent);

    const backendAction = this.$backend.user.create(agent);
    if (awaitBackend) {
      await backendAction;
    }
    return agent;
  },
  async updateAgent(update, options = {}) {
    const { awaitBackend = false } = options;
    const agent = this.getUserByDuid(update.duid);
    if (!agent) {
      return undefined;
    }

    Object.assign(agent, update);
    updateInSorted(this._duidsToUsers, this._sortedUserDuids, agent.duid, userComparator);

    const backendAction = this.$backend.user.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return agent;
  },
  async deleteAgent(agent, options = {}) {
    const { awaitBackend = false } = options;

    const userPropertyDuids = this.getPropertyByKind(PropertyKind.USER)
      .map((e) => e.duid)
      .concat([this.defaultAssigneesProperty.duid, this.defaultUpdatedByProperty.duid]);

    this.$updateLayoutsRemoveEntityDuidOrFiltersNoBackend([agent.duid], userPropertyDuids);
    this.$updateDartboardsPropertyDefaultNoBackend([agent.duid], userPropertyDuids);

    this.$deleteUser(agent);

    const backendAction = this.$backend.user.update({ duid: agent.duid, status: UserStatus.DEACTIVATED });
    if (awaitBackend) {
      await backendAction;
    }
  },
  async createAgentWorkflow(agentDuid, partialWorkflow = {}, options = {}) {
    const { awaitBackend = false } = options;

    const agent = this.getUserByDuid(agentDuid);
    if (!agent) {
      return;
    }

    const workflow = reactive<AgentWorkflow>({
      duid: makeDuid(),
      trigger: EventKind.TASK_UPDATE_ASSIGNEES,
      action: AgentWorkflowAction.POST,
      params: {
        url: "",
        headers: [],
        body: "",
      },
      ...partialWorkflow,
    });
    agent.workflows.push(workflow);

    const backendAction = this.$backend.agentWorkflow.create({ ...workflow, agentDuid });
    if (awaitBackend) {
      await backendAction;
    }
  },
  async updateAgentWorkflow(agentDuid, update, options = {}) {
    const { awaitBackend = false } = options;

    const agent = this.getUserByDuid(agentDuid);
    if (!agent) {
      return;
    }
    const workflow = agent.workflows.find((e) => e.duid === update.duid);
    if (!workflow) {
      return;
    }

    Object.assign(workflow, update);

    const backendAction = this.$backend.agentWorkflow.update(update);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async deleteAgentWorkflow(agentDuid, duid, options = {}) {
    const { awaitBackend = false } = options;

    const agent = this.getUserByDuid(agentDuid);
    if (!agent) {
      return;
    }

    agent.workflows = agent.workflows.filter((e) => e.duid !== duid);

    const backendAction = this.$backend.agentWorkflow.delete(duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setUsers(users) {
    setObjectMapAndSorted(this._duidsToUsers, this._sortedUserDuids, users, userComparator);
  },
  $createOrUpdateUser(user) {
    const userStore = this.$useUserStore();

    const currentUser = this.getUserByDuid(user.duid);

    if (!currentUser) {
      this._duidsToUsers.set(user.duid, user);
    } else {
      Object.assign(currentUser, user);
    }
    updateInSorted(this._duidsToUsers, this._sortedUserDuids, user.duid, userComparator);

    if (user.duid === userStore.duid) {
      userStore.setUser(user);
    }
  },
  $deleteUser(user) {
    const userStore = this.$useUserStore();

    if (user.duid === userStore.duid) {
      userStore.forceLogout();
      return;
    }

    this._duidsToUsers.delete(user.duid);
    removeFromSorted(this._sortedUserDuids, user.duid);
  },
};

export { actions, getters };
